import React, { useRef } from 'react';
import { Copy, Download, FileText, Check, AlertTriangle, Sheet } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import ExcelJS from 'exceljs';
import toast from 'react-hot-toast';
import { useModalStore } from '@/stores';
import { ProviderDetailModal } from '@/components/providers';
import type {
  QuotationData,
  QuotationTable as QTable,
  QuotationLineItem,
  FinalCostTable,
} from '@/types/chat';
import styles from './QuotationTable.module.css';

interface QuotationTableProps {
  quotation: QuotationData;
}

const CURRENCY_CONFIG: Record<string, { symbol: string; locale: string }> = {
  PEN: { symbol: 'S/', locale: 'es-PE' },
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'es-ES' },
  MXN: { symbol: '$', locale: 'es-MX' },
  COP: { symbol: '$', locale: 'es-CO' },
  CLP: { symbol: '$', locale: 'es-CL' },
  ARS: { symbol: '$', locale: 'es-AR' },
};

function formatCurrency(amount: number | undefined | null, currency: string): string {
  const cfg = CURRENCY_CONFIG[currency] ?? CURRENCY_CONFIG.PEN;
  const safeAmount = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
  const formatted = safeAmount.toLocaleString(cfg.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${cfg.symbol} ${formatted}`;
}

function getTableType(q: QuotationData): 'final_cost' | 'comparison' | 'legacy' {
  if (q.table_type === 'final_cost' && q.final_cost && Array.isArray(q.final_cost.rows) && q.final_cost.rows.length > 0)
    return 'final_cost';
  if (Array.isArray(q.tables) && q.tables.length > 0) return 'comparison';
  return 'legacy';
}


// ── Export helpers ──

function toMarkdownTable(quotation: QuotationData): string {
  const tableType = getTableType(quotation);
  const lines: string[] = [`# ${quotation.title || 'Quotation'}`, ''];

  if (tableType === 'final_cost' && quotation.final_cost) {
    const fc = quotation.final_cost;
    lines.push('| Service | Provider | Base Price | Margin | Total |');
    lines.push('|---|---|---|---|---|');
    for (const row of fc.rows) {
      lines.push(
        `| ${row.service} | ${row.provider_name} | ${formatCurrency(row.base_price * row.quantity, row.currency)} | ${row.margin_pct}% | ${formatCurrency(row.total_with_margin, row.currency)} |`,
      );
    }
    lines.push('');
    lines.push(`**Subtotal:** ${formatCurrency(fc.subtotal, fc.currency)}`);
    lines.push(`**Margin (${fc.default_margin_pct}%):** ${formatCurrency(fc.total_margin, fc.currency)}`);
    lines.push(`**Grand Total:** ${formatCurrency(fc.grand_total, fc.currency)}`);
  } else if (tableType === 'comparison') {
    for (const table of quotation.tables ?? []) {
      lines.push(`## ${table.name}`, '');
      const headers = ['Servicio', ...(table.options ?? []).map((o) => `${o.name} (${o.description})`)];
      lines.push(`| ${headers.join(' | ')} |`);
      lines.push(`| ${headers.map(() => '---').join(' | ')} |`);

      for (const row of table.rows ?? []) {
        const cells = (row.cells ?? []).map((c) => {
          const marginStr = c.margin_pct > 0 ? ` (+${c.margin_pct}%)` : '';
          return `${c.provider_name}: ${formatCurrency(c.total, c.currency)}${marginStr}`;
        });
        lines.push(`| ${row.service} | ${cells.join(' | ')} |`);
      }

      lines.push('');
      const totalCells = (table.totals ?? []).map((t) => `**${formatCurrency(t, table.currency)}**`);
      lines.push(`| **Total** | ${totalCells.join(' | ')} |`);
      lines.push('');
    }
  } else if (quotation.line_items) {
    lines.push('| Provider | Service | Unit Price | Qty | Total |');
    lines.push('|---|---|---|---|---|');
    for (const item of quotation.line_items) {
      lines.push(
        `| ${item.provider_name} | ${item.service} | ${formatCurrency(item.unit_price, item.currency)} | ${item.quantity} | ${formatCurrency(item.total, item.currency)} |`,
      );
    }
    lines.push('', `**Total:** ${formatCurrency(quotation.grand_total ?? 0, quotation.currency)}`);
  }

  return lines.join('\n');
}

async function buildQuotationXlsx(quotation: QuotationData): Promise<Uint8Array> {
  const wb = new ExcelJS.Workbook();
  const tableType = getTableType(quotation);

  if (tableType === 'final_cost' && quotation.final_cost) {
    const fc = quotation.final_cost;
    const ws = wb.addWorksheet('Final Cost');
    ws.columns = [
      { header: 'Service', key: 'service', width: 22 },
      { header: 'Provider', key: 'provider', width: 22 },
      { header: 'Base Price', key: 'base_price', width: 14 },
      { header: 'Margin (%)', key: 'margin_pct', width: 12 },
      { header: 'Margin ($)', key: 'margin_amount', width: 14 },
      { header: 'Total', key: 'total', width: 14 },
    ];
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: 'FF0C1B33' },
    };

    for (const row of fc.rows) {
      ws.addRow({
        service: row.service,
        provider: row.provider_name,
        base_price: row.base_price * row.quantity,
        margin_pct: row.margin_pct,
        margin_amount: row.margin_amount,
        total: row.total_with_margin,
      });
    }

    ws.addRow({});
    const subtotalRow = ws.addRow({ service: 'Subtotal', total: fc.subtotal });
    subtotalRow.font = { bold: true };
    const marginRow = ws.addRow({
      service: `Margin (${fc.default_margin_pct}%)`,
      total: fc.total_margin,
    });
    marginRow.font = { italic: true };
    const grandTotalRow = ws.addRow({ service: 'Grand Total', total: fc.grand_total });
    grandTotalRow.font = { bold: true, size: 12 };
    grandTotalRow.fill = {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: 'FFF0F4FF' },
    };

    ws.getColumn('base_price').numFmt = '#,##0.00';
    ws.getColumn('margin_amount').numFmt = '#,##0.00';
    ws.getColumn('total').numFmt = '#,##0.00';
  } else if (tableType === 'comparison') {
    for (const table of quotation.tables ?? []) {
      const ws = wb.addWorksheet(table.name.slice(0, 31));
      const columns = [
        { header: 'Servicio', key: 'service', width: 20 },
        ...(table.options ?? []).map((o, i) => ({
          header: `${o.name} - ${o.description}`,
          key: `opt_${i}`,
          width: 28,
        })),
      ];
      ws.columns = columns;

      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern' as const,
        pattern: 'solid' as const,
        fgColor: { argb: 'FF0C1B33' },
      };
      headerRow.alignment = { vertical: 'middle' };

      for (const row of table.rows ?? []) {
        const rowData: Record<string, string | number> = { service: row.service };
        (row.cells ?? []).forEach((c, i) => {
          rowData[`opt_${i}`] = c.total;
        });
        ws.addRow(rowData);
      }

      ws.addRow({});
      const totalData: Record<string, string | number> = { service: 'TOTAL' };
      (table.totals ?? []).forEach((t, i) => {
        totalData[`opt_${i}`] = t;
      });
      const totalRow = ws.addRow(totalData);
      totalRow.font = { bold: true, size: 12 };
      totalRow.fill = {
        type: 'pattern' as const,
        pattern: 'solid' as const,
        fgColor: { argb: 'FFF0F4FF' },
      };

      (table.options ?? []).forEach((_, i) => {
        ws.getColumn(`opt_${i}`).numFmt = '#,##0.00';
      });
    }
  } else if (quotation.line_items) {
    const ws = wb.addWorksheet('Quotation');
    ws.columns = [
      { header: 'Provider', key: 'provider', width: 22 },
      { header: 'Service', key: 'service', width: 18 },
      { header: 'Description', key: 'description', width: 30 },
      { header: 'Unit', key: 'unit', width: 14 },
      { header: 'Qty', key: 'qty', width: 8 },
      { header: 'Unit Price', key: 'unit_price', width: 14 },
      { header: 'Total', key: 'total', width: 14 },
    ];
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern' as const,
      pattern: 'solid' as const,
      fgColor: { argb: 'FF0C1B33' },
    };

    for (const item of quotation.line_items) {
      ws.addRow({
        provider: item.provider_name,
        service: item.service,
        description: item.description,
        unit: item.unit,
        qty: item.quantity,
        unit_price: item.unit_price,
        total: item.total,
      });
    }
    ws.addRow({});
    const totalRow = ws.addRow({ unit_price: 'Total', total: quotation.grand_total });
    totalRow.font = { bold: true };
  }

  const buffer = await wb.xlsx.writeBuffer();
  return new Uint8Array(buffer as ArrayBuffer);
}

async function uploadToGoogleSheets(
  quotation: QuotationData,
  accessToken: string,
): Promise<string> {
  const xlsxBytes = await buildQuotationXlsx(quotation);
  const fileName = `${(quotation.title || 'Quotation').replace(/[^a-zA-Z0-9 ]/g, '').trim()}`;

  const metadata = {
    name: fileName,
    mimeType: 'application/vnd.google-apps.spreadsheet',
  };

  const boundary = 'ebombo_sheets_boundary';
  const body = new Uint8Array(
    await new Blob([
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
      JSON.stringify(metadata),
      `\r\n--${boundary}\r\nContent-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet\r\n\r\n`,
      xlsxBytes,
      `\r\n--${boundary}--\r\n`,
    ]).arrayBuffer(),
  );

  const resp = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    },
  );

  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Drive upload failed: ${err}`);
  }

  const fileData = (await resp.json()) as { id: string };
  return `https://docs.google.com/spreadsheets/d/${fileData.id}/edit`;
}

function handleDownloadCsv(quotation: QuotationData) {
  let rows: string[][];
  const tableType = getTableType(quotation);

  if (tableType === 'final_cost' && quotation.final_cost) {
    const fc = quotation.final_cost;
    rows = [
      ['Service', 'Provider', 'Base Price', 'Margin (%)', 'Margin ($)', 'Total'],
      ...fc.rows.map((row) => [
        row.service,
        row.provider_name,
        String(row.base_price * row.quantity),
        String(row.margin_pct),
        String(row.margin_amount),
        String(row.total_with_margin),
      ]),
      [],
      ['', '', '', '', 'Subtotal', String(fc.subtotal)],
      ['', '', '', '', `Margin (${fc.default_margin_pct}%)`, String(fc.total_margin)],
      ['', '', '', '', 'Grand Total', String(fc.grand_total)],
    ];
  } else if (tableType === 'comparison') {
    const table = quotation.tables![0];
    rows = [
      ['Servicio', ...(table.options ?? []).map((o) => `${o.name} (${o.description})`)],
      ...(table.rows ?? []).map((r) => [
        r.service,
        ...(r.cells ?? []).map((c) => `${c.provider_name}: ${c.total}`),
      ]),
      [],
      ['Total', ...(table.totals ?? []).map((t) => String(t))],
    ];
  } else {
    rows = [
      ['Provider', 'Service', 'Description', 'Unit', 'Qty', 'Unit Price', 'Total'],
      ...(quotation.line_items ?? []).map((item) => [
        item.provider_name,
        item.service,
        item.description,
        item.unit,
        String(item.quantity),
        String(item.unit_price),
        String(item.total),
      ]),
      [],
      ['', '', '', '', '', 'Total', String(quotation.grand_total ?? 0)],
    ];
  }

  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(quotation.title || 'Quotation').replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Final cost table renderer ──

function FinalCostTableView({ data }: { data: FinalCostTable }) {
  if (!data.rows || data.rows.length === 0) return null;

  return (
    <div className={styles.finalCostTable}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Service</th>
              <th>Provider</th>
              <th className={styles.numCol}>Base Price</th>
              <th className={styles.numCol}>Margin</th>
              <th className={styles.numCol}>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? styles.evenRow : styles.oddRow}>
                <td className={styles.serviceCell}>{row.service}</td>
                <td>{row.provider_name}</td>
                <td className={styles.numCol}>
                  {formatCurrency(row.base_price * row.quantity, row.currency)}
                </td>
                <td className={styles.numCol}>{row.margin_pct}%</td>
                <td className={`${styles.numCol} ${styles.totalCell}`}>
                  {formatCurrency(row.total_with_margin, row.currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={styles.subtotalRow}>
              <td colSpan={4} className={styles.footLabel}>
                Subtotal
              </td>
              <td className={styles.numCol}>{formatCurrency(data.subtotal, data.currency)}</td>
            </tr>
            <tr className={styles.marginRow}>
              <td colSpan={4} className={styles.footLabel}>
                Margin ({data.default_margin_pct ?? 20}%)
              </td>
              <td className={styles.numCol}>{formatCurrency(data.total_margin, data.currency)}</td>
            </tr>
            <tr className={styles.grandTotalRow}>
              <td colSpan={4} className={styles.footLabel}>
                Grand Total
              </td>
              <td className={styles.numCol}>{formatCurrency(data.grand_total, data.currency)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Legacy table renderer ──

function LegacyTable({ quotation }: { quotation: QuotationData }) {
  const grouped = new Map<string, QuotationLineItem[]>();
  for (const item of quotation.line_items ?? []) {
    if (!grouped.has(item.provider_name)) grouped.set(item.provider_name, []);
    grouped.get(item.provider_name)!.push(item);
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Service</th>
            <th>Description</th>
            <th>Unit</th>
            <th className={styles.numCol}>Qty</th>
            <th className={styles.numCol}>Unit Price</th>
            <th className={styles.numCol}>Margin</th>
            <th className={styles.numCol}>Total</th>
          </tr>
        </thead>
        <tbody>
          {Array.from(grouped.entries()).map(([providerName, items], gi) => (
            <React.Fragment key={providerName}>
              {gi > 0 && (
                <tr className={styles.separatorRow}>
                  <td colSpan={8} />
                </tr>
              )}
              {items.map((item, ii) => (
                <tr key={item.id} className={ii % 2 === 0 ? styles.evenRow : styles.oddRow}>
                  {ii === 0 && (
                    <td className={styles.providerCell} rowSpan={items.length}>
                      {providerName}
                    </td>
                  )}
                  <td className={styles.serviceCell}>{item.service}</td>
                  <td className={styles.descCell}>
                    {item.description}
                    {item.notes && <span className={styles.noteTag}>{item.notes}</span>}
                  </td>
                  <td>{item.unit}</td>
                  <td className={styles.numCol}>{item.quantity}</td>
                  <td className={styles.numCol}>
                    {formatCurrency(item.unit_price, item.currency)}
                  </td>
                  <td className={styles.numCol}>
                    {item.margin_pct > 0 ? (
                      `${item.margin_pct}%`
                    ) : item.margin_amount > 0 ? (
                      formatCurrency(item.margin_amount, item.currency)
                    ) : (
                      <span className={styles.dash}>—</span>
                    )}
                  </td>
                  <td className={styles.numCol}>{formatCurrency(item.total, item.currency)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className={styles.subtotalRow}>
            <td colSpan={7} className={styles.footLabel}>
              Subtotal
            </td>
            <td className={styles.numCol}>
              {formatCurrency(quotation.subtotal ?? 0, quotation.currency)}
            </td>
          </tr>
          {(quotation.overall_margin_pct ?? 0) > 0 && (
            <tr className={styles.marginRow}>
              <td colSpan={7} className={styles.footLabel}>
                Overall Margin ({quotation.overall_margin_pct}%)
              </td>
              <td className={styles.numCol}>
                {formatCurrency(quotation.overall_margin_amount, quotation.currency)}
              </td>
            </tr>
          )}
          <tr className={styles.grandTotalRow}>
            <td colSpan={7} className={styles.footLabel}>
              Grand Total
            </td>
            <td className={styles.numCol}>
              {formatCurrency(quotation.grand_total ?? 0, quotation.currency)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Options table renderer ──

function OptionsTable({ table, currency }: { table: QTable; currency: string }) {
  const cur = table.currency || currency;
  const { openModal } = useModalStore();

  const handleProviderClick = (providerId: string, providerName: string) => {
    openModal(
      <ProviderDetailModal
        provider={{
          id: providerId,
          providerId: providerId,
          name: providerName,
          city: null,
          district: null,
          rating: null,
          image: null,
          services: [],
          serviceCount: 0,
          priceDisplay: 'A consultar',
          capacity: null,
        }}
      />,
    );
  };

  return (
    <div className={styles.optionsSection}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.serviceHeader}>Service</th>
              {(table.options ?? []).map((opt, i) => (
                <th key={i} className={styles.optionHeader}>
                  <div className={styles.optionName}>{opt.name}</div>
                  <div className={styles.optionDesc}>{opt.description}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(table.rows ?? []).map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? styles.evenRow : styles.oddRow}>
                <td className={styles.serviceCell}>{row.service}</td>
                {(row.cells ?? []).map((cell, ci) => (
                  <td key={ci} className={styles.optionCell}>
                    <button
                      className={styles.providerLink}
                      onClick={() => handleProviderClick(cell.provider_id, cell.provider_name)}
                      title={`View details for ${cell.provider_name}`}
                    >
                      {cell.provider_name}
                    </button>
                    <div className={styles.cellPrice}>
                      {formatCurrency(cell.total, cell.currency || cur)}
                    </div>
                    {cell.margin_pct > 0 && (
                      <div className={styles.cellMargin}>+{cell.margin_pct}%</div>
                    )}
                    {cell.notes && <div className={styles.cellNote}>{cell.notes}</div>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={styles.grandTotalRow}>
              <td className={styles.footLabel}>Total</td>
              {(table.totals ?? []).map((total, i) => (
                <td key={i} className={styles.numCol}>
                  {formatCurrency(total, cur)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Single card with header + actions + content ──

interface QuotationCardProps {
  quotation: QuotationData;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const QuotationCard: React.FC<QuotationCardProps> = ({ quotation, title, subtitle, children }) => {
  const [copied, setCopied] = React.useState(false);
  const [isSheetsExporting, setIsSheetsExporting] = React.useState(false);
  const [sheetsUrl, setSheetsUrl] = React.useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const googleLoginForSheets = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsSheetsExporting(true);
      toast.loading('Exporting to Google Sheets...', { id: `sheets-${title}` });
      try {
        const sheetUrl = await uploadToGoogleSheets(quotation, tokenResponse.access_token);
        setSheetsUrl(sheetUrl);
        toast.success('Done! Click "Open" to view your sheet.', {
          id: `sheets-${title}`,
          duration: 8000,
        });
      } catch {
        toast.error('Error exporting to Google Sheets', { id: `sheets-${title}` });
      } finally {
        setIsSheetsExporting(false);
      }
    },
    onError: () => {
      toast.error('Google sign-in cancelled');
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(toMarkdownTable(quotation));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className={styles.container} ref={tableRef}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <FileText size={16} className={styles.titleIcon} />
          <div>
            <h3 className={styles.title}>{title}</h3>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
        <div className={styles.actions}>
          <button onClick={handleCopy} className={styles.actionBtn} title="Copy as Markdown">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          <button
            onClick={() => handleDownloadCsv(quotation)}
            className={styles.actionBtn}
            title="Download CSV"
          >
            <Download size={14} />
            <span>CSV</span>
          </button>
          <button onClick={handlePrint} className={styles.actionBtn} title="Print / Save as PDF">
            <FileText size={14} />
            <span>PDF</span>
          </button>
          {sheetsUrl ? (
            <a
              href={sheetsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.actionBtn} ${styles.sheetsBtn} ${styles.sheetsBtnReady}`}
              title="Open in Google Sheets"
            >
              <Sheet size={14} />
              <span>Abrir</span>
            </a>
          ) : (
            <button
              onClick={() => googleLoginForSheets()}
              className={`${styles.actionBtn} ${styles.sheetsBtn}`}
              title="Export to Google Sheets"
              disabled={isSheetsExporting}
            >
              <Sheet size={14} />
              <span>{isSheetsExporting ? 'Exporting...' : 'Sheets'}</span>
            </button>
          )}
        </div>
      </div>

      {quotation.is_alternative && (
        <div className={styles.altWarning}>
          <AlertTriangle size={14} />
          <span>
            No confirmed providers were found for the requested location. Showing providers from other
            cities.
          </span>
        </div>
      )}

      {children}
    </div>
  );
};

// ── Main component ──

export const QuotationTable: React.FC<QuotationTableProps> = ({ quotation }) => {
  if (!quotation || typeof quotation !== 'object') return null;

  const hasComparison = Array.isArray(quotation.tables) && quotation.tables.length > 0;
  const hasFinalCost =
    quotation.final_cost && Array.isArray(quotation.final_cost.rows) && quotation.final_cost.rows.length > 0;

  // Both tables exist — one card per service comparison + one final cost card
  if (hasComparison && hasFinalCost) {
    const tables = quotation.tables ?? [];
    const finalCostQuotation: QuotationData = {
      ...quotation,
      tables: undefined,
      table_type: 'final_cost',
    };
    const margin = quotation.final_cost?.default_margin_pct ?? 20;

    return (
      <div className={styles.splitQuotation}>
        <p className={styles.sectionLabel}>
          Comparación de proveedores por servicio — haz clic en un proveedor para ver su ficha completa.
        </p>

        {tables.map((table, i) => {
          const singleTableQuotation: QuotationData = {
            ...quotation,
            tables: [table],
            final_cost: undefined,
            table_type: 'comparison',
          };
          return (
            <QuotationCard
              key={i}
              quotation={singleTableQuotation}
              title={table.name}
              subtitle={`${(table.options ?? []).length} proveedores encontrados`}
            >
              <OptionsTable table={table} currency={quotation.currency} />
            </QuotationCard>
          );
        })}

        <p className={styles.sectionLabel}>
          Cotización final con la selección recomendada y margen de {margin}% aplicado.
          Puedes pedir ajustes de margen o cambiar proveedores.
        </p>

        <QuotationCard
          quotation={finalCostQuotation}
          title="Cotización Final Recomendada"
          subtitle={`Margen de ${margin}% aplicado`}
        >
          <FinalCostTableView data={quotation.final_cost!} />
        </QuotationCard>
      </div>
    );
  }

  // Only comparison — one card per service
  if (hasComparison) {
    const tables = quotation.tables ?? [];
    if (tables.length === 1) {
      return (
        <QuotationCard
          quotation={quotation}
          title={tables[0].name || quotation.title || 'Comparación de Proveedores'}
          subtitle={`${(tables[0].options ?? []).length} proveedores encontrados`}
        >
          <OptionsTable table={tables[0]} currency={quotation.currency} />
        </QuotationCard>
      );
    }
    return (
      <div className={styles.splitQuotation}>
        <p className={styles.sectionLabel}>
          Comparación de proveedores por servicio
        </p>

        {tables.map((table, i) => {
          const singleTableQuotation: QuotationData = {
            ...quotation,
            tables: [table],
            table_type: 'comparison',
          };
          return (
            <QuotationCard
              key={i}
              quotation={singleTableQuotation}
              title={table.name}
              subtitle={`${(table.options ?? []).length} proveedores encontrados`}
            >
              <OptionsTable table={table} currency={quotation.currency} />
            </QuotationCard>
          );
        })}
      </div>
    );
  }

  // Only final cost
  if (hasFinalCost && quotation.final_cost) {
    return (
      <QuotationCard
        quotation={quotation}
        title={quotation.title || 'Final Quotation'}
        subtitle={
          quotation.event_details
            ? [
                quotation.event_details.type,
                quotation.event_details.guests > 0 ? `${quotation.event_details.guests} guests` : '',
                quotation.event_details.location,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
      >
        <FinalCostTableView data={quotation.final_cost} />
      </QuotationCard>
    );
  }

  // Legacy fallback
  return (
    <QuotationCard
      quotation={quotation}
      title={quotation.title || 'Quotation'}
      subtitle={
        quotation.event_details
          ? [
              quotation.event_details.type,
              quotation.event_details.guests > 0 ? `${quotation.event_details.guests} guests` : '',
              quotation.event_details.location,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
    >
      <LegacyTable quotation={quotation} />
    </QuotationCard>
  );
};
